import axios from 'axios'
import {
  classifyAuthFailure, applyRenewedSession, loginHrefFor, endSessionLocally, sessionEndAfterFailedRenewal,
} from '../utils/session_renewal'

const default_error_message = 'Error accesing the requested resource.'
const default_status_code = 400
const success_alert = {message: 'Success!', variant:'success', timer: 1000}
const error_alert = {message: default_error_message, variant:'danger'}

export const client = axios.create({
    json: true
})

// FI-2180: what an auth rejection means for the session, then act on it.
//
// 'expired' | 'invalid': the session is over. remember why (login.vue reads
// it once and says so) and bounce to the login route carrying the current
// hash path, so login.vue brings the user straight back. nothing is cleared
// here: login.vue owns the clear, and clearing early would race any other
// request still in flight.
//
// 'forbidden': the jwt is still valid by its own exp, so this is either a
// real permission denial or a tenant route that still answers 403 for an
// invalidated token. one shared refresh tells the two apart: a renewed token
// means the original request is worth one retry; a 401 from the refresh
// means the session is over after all.
const REFRESH_RESOURCE = 'bookings/auth/refresh'
const REFRESH_MIN_GAP_MS = 30000
let refreshInFlight = null
let lastRefreshAt = 0

const endSession = (kind) => {
    endSessionLocally(localStorage, sessionStorage, kind)
    const href = loginHrefFor(window.location.hash)
    if (href) window.location.href = href
}

// the denial stood: tell the shell so it can say so gently (App.vue listens)
const announceDenied = (resource) => {
    window.dispatchEvent(new CustomEvent('fi-auth-denied', { detail: { resource } }))
}

// the one retry after a successful refresh. a 403 from the retry is a real
// permission denial, so it gets the same notice as an unrefreshable one.
const retryOnce = async (request, resource) => {
    const retry = { ...request, headers: { ...request.headers, Authorization: `Bearer ${localStorage.getItem('jwt')}` } }
    try {
        return await client(retry)
    } catch (retryErr) {
        if (classify(retryErr, localStorage.getItem('jwt')) === 'forbidden') announceDenied(resource)
        throw retryErr
    }
}

const classify = (e, accessToken) => {
    const body = e.response && e.response.data
    // a blob body (download_file) has no envelope to read
    const isBlob = typeof Blob !== 'undefined' && body instanceof Blob
    const envelope = body && typeof body === 'object' && !isBlob ? body : null
    // FI-2274: a json reply without the api's envelope (a bare jsonify 403
    // from a public link or an admin-only route) says nothing about the
    // session, so its http status is not passed on: prod only ever reacted to
    // an envelope status_code. a blob keeps its http status
    const bare = !isBlob && !(envelope && envelope.status_code != null)
    return classifyAuthFailure({
        httpStatus: bare ? undefined : e.response && e.response.status,
        bodyStatusCode: envelope && envelope.status_code,
        errorCode: envelope && envelope.error_code,
        jwt: accessToken,
        nowMs: Date.now(),
    })
}

// one refresh for every concurrent caller, and at most one attempt per 30s.
// resolves to the renewed {token, user} payload, or null when an attempt was
// made recently enough that another would tell us nothing new. the call goes
// through the shared axios client directly rather than apis.js (which
// imports this module) to keep the import graph acyclic.
const refreshSessionOnce = () => {
    if (refreshInFlight) return refreshInFlight
    const now = Date.now()
    if (now - lastRefreshAt < REFRESH_MIN_GAP_MS) return Promise.resolve(null)
    lastRefreshAt = now
    const accessToken = localStorage.getItem('jwt')
    const settle = () => { refreshInFlight = null }
    refreshInFlight = client({
        method: 'POST',
        baseURL: process.env.VUE_APP_API_URL,
        url: REFRESH_RESOURCE,
        data: {},
        headers: { Authorization: `Bearer ${accessToken}` },
    }).then(({ data }) => {
        settle()
        const payload = data && data.data
        // applyRenewedSession throws on a reply without token and user
        applyRenewedSession(localStorage, payload || {})
        return payload
    }, (err) => {
        settle()
        throw err
    })
    return refreshInFlight
}

// a 403 with a locally valid jwt. returns true when the session was renewed
// (retry the original request), false when the denial stands. a refresh that
// says the token itself is no good (401, error_code 3) ends the session here;
// a refusal to extend a token that is still valid (409/9, FI-2273) does not.
const recoverForbidden = async (e, resource) => {
    let renewed = null
    try {
        renewed = await refreshSessionOnce()
    } catch (refreshErr) {
        const body = refreshErr && refreshErr.response && refreshErr.response.data
        const kind = sessionEndAfterFailedRenewal({
            httpStatus: refreshErr && refreshErr.response && refreshErr.response.status,
            errorCode: body && typeof body === 'object' ? body.error_code : undefined,
            jwt: localStorage.getItem('jwt'),
            nowMs: Date.now(),
        })
        if (kind) {
            endSession(kind)
            return false
        }
        // eslint-disable-next-line no-console
        console.log('session refresh failed: ' + ((refreshErr && refreshErr.message) || refreshErr))
    }
    if (renewed) return true
    announceDenied(resource)
    return false
}

const execute = async(method, {service, resource}, data, config) => {
    // FI-2173: noAuthRedirect is ours, not axios's. a background soft pass
    // (erm live refresh) sets it so a 403 rejects without wiping the session
    // out from under a reader; every other caller keeps the bounce below.
    const { noAuthRedirect, ...requestConfig } = config || {}
    const accessToken = localStorage.getItem('jwt')
    const request = {
        method,
        baseURL: service,
        url: resource,
        data,
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        ...requestConfig
    }
    try{
        const { data } = await client(request)
        return data
    } catch(e){
        const data = e.response && e.response.data
        error_alert.message = (data && data.message) ||
            (data && data.data && data.data.error) ||
            default_error_message

        let status_code = data && data.status_code != null ? data.status_code : default_status_code

        console.log("ex:" + status_code + ":" + error_alert.message)

        if (!noAuthRedirect) {
            const kind = classify(e, accessToken)
            if (kind === 'expired' || kind === 'invalid') {
                endSession(kind)
            } else if (kind === 'forbidden' && await recoverForbidden(e, resource)) {
                // renewed: the original request gets ONE more go with the new
                // bearer. a second failure is the caller's, whatever it is.
                const { data: retried } = await retryOnce(request, resource)
                return retried
            }
        }

        throw e
    }
}

export const execute_generator = async function*(method, {service, resource}, payload, config) {
    const { data } = await execute(method, {service, resource}, payload, config)
    yield data
    ? {alert: success_alert, data: data}
    : {alert: error_alert}
}

// FI-1932: authenticated file download. mirrors execute()'s auth header and
// baseURL but returns the raw axios response (blob body + headers) instead of
// unwrapping a json envelope, which a binary body has no shape for. the caller
// reads the filename off content-disposition and turns the blob into a
// download. a 403 is classified like the json path (FI-2180): an expired jwt
// still bounces to login rather than saving an error blob, a live one gets
// the same one-shot refresh and retry.
export const download_file = async (method, { service, resource }, config) => {
    const accessToken = localStorage.getItem('jwt')
    const request = {
        method,
        baseURL: service,
        url: resource,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${accessToken}` },
        ...(config || {}),
    }
    try {
        return await client(request)
    } catch (e) {
        const kind = classify(e, accessToken)
        if (kind === 'expired' || kind === 'invalid') {
            endSession(kind)
        } else if (kind === 'forbidden' && await recoverForbidden(e, resource)) {
            return await retryOnce(request, resource)
        }
        throw e
    }
}
